import React, { Fragment } from 'react';
import { Box, VStack, Text, HStack, Icon } from '@chakra-ui/react';
import { FiFileText, FiDownload } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetInvoicesQuery, useGetPaymentServicesQuery } from '../../api';
import PageHeader from '../../components/base/PageHeader';
import LoadingState from '../../components/base/LoadingState';
import DateFilterBar from '../../components/base/DateFilterBar';
import { useDateFilter } from '../../hooks/useDateFilter';

const ChargesHistoryPage: React.FC = () => {
    const { t, locale } = useTranslation();
    const { data: invoicesData, isLoading } = useGetInvoicesQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const { data: paymentServicesData, isLoading: isLoadingServices } = useGetPaymentServicesQuery(
        undefined,
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const extraChargesTotal =
        paymentServicesData?.services?.reduce((sum, s) => sum + (s.paid_this_month ?? 0), 0) ?? 0;

    const allInvoices = invoicesData || [];
    const { filteredItems, dateFilter, handleSearchByDate, handleClearFilter } = useDateFilter({
        items: allInvoices,
        getDateFromItem: (invoice) => invoice.data,
    });

    const formatInvoiceDate = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(date);
    };

    const handleDownload = (fileUrl: string) => {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
    };

    if (isLoading || isLoadingServices) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    return (
        <Box
            h="calc(100vh - 20px)"
            bg="gray.100"
            px={4}
            pt={2}
            maxW="4xl"
            pb={{ base: 20, lg: 6 }}
            overflow="auto"
        >
            <PageHeader title={t.pages.payment.chargesHistory} backPath="/payment" />

            <VStack gap={6} align="stretch" w={{ base: 'full', md: '600px' }} mx="auto">
                {/* Списания за доп. услуги в этом месяце */}
                <Box p={6} bg="white">
                    <Text fontSize="lg" fontWeight="700" color="black" mb={4}>
                        {t.pages.payment.extraChargesSectionTitle}
                    </Text>
                    <VStack align="stretch" gap={1} w="full">
                        {paymentServicesData?.services?.map((service) => (
                            <HStack
                                key={service.type}
                                justify="space-between"
                                fontSize="md"
                                color="black"
                                w="full"
                            >
                                <Text>{t.pages.payment.extraServiceTypes[service.type]}</Text>
                                <Text>€{(service.paid_this_month ?? 0).toFixed(2)}</Text>
                            </HStack>
                        ))}
                        <HStack
                            justify="space-between"
                            fontSize="md"
                            color="black"
                            fontWeight="medium"
                            mt={2}
                            pt={1}
                            borderTopWidth="1px"
                            borderColor="gray.300"
                            w="full"
                        >
                            <Text>{t.pages.payment.extraChargesTotalLabel}</Text>
                            <Text>€{extraChargesTotal.toFixed(2)}</Text>
                        </HStack>
                    </VStack>
                </Box>

                {/* Список фактур */}
                <Box p={6} bg="white">
                    <VStack gap={4} align="stretch" w="full">
                        <DateFilterBar
                            dateFilter={dateFilter}
                            onSearchByDate={handleSearchByDate}
                            onClearFilter={handleClearFilter}
                        />
                        {filteredItems.length === 0 ? (
                            <Text fontSize="md" color="gray.500" textAlign="center" py={8}>
                                {t.pages.payment.noInvoices}
                            </Text>
                        ) : (
                            <VStack align="stretch" gap={0}>
                                {filteredItems.map((invoice, index) => (
                                    <Fragment key={invoice.id}>
                                        <Box
                                            my={2}
                                            p={4}
                                            _hover={{ bg: 'gray.100' }}
                                            transition="all 0.2s"
                                            cursor="pointer"
                                            onClick={() => handleDownload(invoice.file_url)}
                                        >
                                            <HStack justify="space-between" align="center">
                                                <HStack gap={3} align="center" flex={1}>
                                                    <Icon as={FiFileText} boxSize={5} color="yellow.600" />
                                                    <VStack align="flex-start" gap={0}>
                                                        <Text fontSize="md" fontWeight="medium" color="black">
                                                            {t.pages.payment.invoice}{' '}
                                                            {formatInvoiceDate(invoice.data)}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                                <Icon as={FiDownload} boxSize={5} color="gray.500" />
                                            </HStack>
                                        </Box>
                                        {index < filteredItems.length - 1 && (
                                            <Box h="1px" bg="gray.200" w="full" />
                                        )}
                                    </Fragment>
                                ))}
                            </VStack>
                        )}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ChargesHistoryPage;
